import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, Users, Search, Tag, Filter, Download, X } from 'lucide-react';
import { createList, getLists, deleteList, createContact, getContacts, deleteContact, importCSV, addContactTags, removeContactTag, getAllTags, previewSegment, exportContacts, bulkTagList } from '../lib/api';

export default function ContactLists() {
  const [lists, setLists] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newListName, setNewListName] = useState('');
  const [newListDesc, setNewListDesc] = useState('');
  const [showNewList, setShowNewList] = useState(false);
  const [newContact, setNewContact] = useState({ email: '', first_name: '', last_name: '', company: '' });
  const [showNewContact, setShowNewContact] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Tag management
  const [allTags, setAllTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [tagContactId, setTagContactId] = useState(null);
  const [showTagDialog, setShowTagDialog] = useState(false);
  // Segmentation
  const [showSegment, setShowSegment] = useState(false);
  const [segmentTags, setSegmentTags] = useState([]);
  const [segmentResult, setSegmentResult] = useState(null);

  useEffect(() => { loadLists(); loadAllTags(); }, []);

  useEffect(() => {
    if (selectedList) loadContacts(selectedList);
  }, [selectedList]);

  const loadLists = async () => {
    try {
      const res = await getLists();
      setLists(res.data);
      if (res.data.length > 0 && !selectedList) {
        setSelectedList(res.data[0].id);
      }
    } catch (err) {
      toast.error('Failed to load lists');
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async (listId) => {
    try {
      const res = await getContacts(listId);
      setContacts(res.data);
    } catch (err) {
      toast.error('Failed to load contacts');
    }
  };

  const loadAllTags = async () => {
    try {
      const res = await getAllTags();
      setAllTags(res.data);
    } catch (err) { /* tags may not exist yet */ }
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    try {
      const res = await createList({ name: newListName, description: newListDesc });
      setLists([res.data, ...lists]);
      setSelectedList(res.data.id);
      setNewListName('');
      setNewListDesc('');
      setShowNewList(false);
      toast.success('List created');
    } catch (err) {
      toast.error('Failed to create list');
    }
  };

  const handleDeleteList = async (id) => {
    if (!window.confirm('Delete this list and all contacts?')) return;
    try {
      await deleteList(id);
      setLists(lists.filter(l => l.id !== id));
      if (selectedList === id) {
        setSelectedList(lists.length > 1 ? lists.find(l => l.id !== id)?.id : null);
        setContacts([]);
      }
      toast.success('List deleted');
    } catch (err) {
      toast.error('Failed to delete list');
    }
  };

  const handleAddContact = async () => {
    if (!newContact.email) return;
    try {
      const res = await createContact({ ...newContact, list_id: selectedList });
      setContacts([res.data, ...contacts]);
      setNewContact({ email: '', first_name: '', last_name: '', company: '' });
      setShowNewContact(false);
      toast.success('Contact added');
      loadLists();
    } catch (err) {
      toast.error('Failed to add contact');
    }
  };

  const handleDeleteContact = async (id) => {
    try {
      await deleteContact(id);
      setContacts(contacts.filter(c => c.id !== id));
      loadLists();
      toast.success('Contact deleted');
    } catch (err) {
      toast.error('Failed to delete contact');
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedList) return;
    try {
      const res = await importCSV(file, selectedList);
      toast.success(`Imported ${res.data.imported} contacts (${res.data.errors} errors)`);
      loadContacts(selectedList);
      loadLists();
    } catch (err) {
      toast.error('Failed to import CSV');
    }
    e.target.value = '';
  };

  const handleAddTag = async () => {
    if (!newTag.trim() || !tagContactId) return;
    try {
      await addContactTags(tagContactId, [newTag.trim()]);
      toast.success(`Tag "${newTag}" added`);
      setNewTag('');
      setShowTagDialog(false);
      loadContacts(selectedList);
      loadAllTags();
    } catch (err) {
      toast.error('Failed to add tag');
    }
  };

  const handleRemoveTag = async (contactId, tag) => {
    try {
      await removeContactTag(contactId, tag);
      toast.success(`Tag "${tag}" removed`);
      loadContacts(selectedList);
      loadAllTags();
    } catch (err) {
      toast.error('Failed to remove tag');
    }
  };

  const handleBulkTag = async (tagName) => {
    if (!selectedList) return;
    try {
      const res = await bulkTagList(selectedList, [tagName]);
      toast.success(`Tag "${tagName}" added to ${res.data.updated} contacts`);
      setNewTag('');
      loadContacts(selectedList);
      loadAllTags();
    } catch (err) {
      toast.error('Failed to bulk-tag contacts');
    }
  };

  const handleSegmentPreview = async () => {
    if (!selectedList || segmentTags.length === 0) return;
    try {
      const res = await previewSegment(selectedList, { tags: segmentTags, subscribed_only: true });
      setSegmentResult(res.data);
    } catch (err) {
      toast.error('Failed to preview segment');
    }
  };

  const handleExportContacts = async () => {
    if (!selectedList) return;
    try {
      const res = await exportContacts(selectedList, 'csv');
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'contacts_export.csv'; a.click();
      toast.success('Contacts exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const toggleSegmentTag = (tag) => {
    setSegmentTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
    setSegmentResult(null);
  };

  const filteredContacts = contacts.filter(c =>
    !searchTerm || 
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.tags || []).some(t => t.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="page-container">
      {/* Hero Header */}
      <div className="page-hero" data-testid="contacts-hero">
        <div className="relative z-10 px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand)/0.15)] border border-[hsl(var(--brand)/0.25)] flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-[hsl(var(--brand))]" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand))] font-semibold mb-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>CRM</p>
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Contacts</h1>
              <p className="text-sm text-white/50 mt-0.5">Manage your contact lists, tags, and segments</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowSegment(!showSegment)} data-testid="segment-toggle-btn" className="border-white/10 text-white/70 hover:bg-white/5 btn-press">
              <Filter className="w-4 h-4 mr-2" /> Segment
            </Button>
            <Dialog open={showNewList} onOpenChange={setShowNewList}>
              <DialogTrigger asChild>
                <Button className="bg-[hsl(var(--accent-warm))] hover:bg-[hsl(var(--accent-warm)/0.88)] text-[hsl(var(--accent-warm-foreground))] btn-press" data-testid="create-list-btn"><Plus className="w-4 h-4 mr-2" /> New List</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create Contact List</DialogTitle><DialogDescription>Add a new list to organize your contacts</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Name</Label>
                    <Input value={newListName} onChange={(e) => setNewListName(e.target.value)}
                      placeholder="e.g., Security Team" data-testid="list-name-input" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input value={newListDesc} onChange={(e) => setNewListDesc(e.target.value)}
                      placeholder="Optional description" />
                  </div>
                  <Button onClick={handleCreateList} className="w-full bg-[hsl(var(--accent-warm))] hover:bg-[hsl(var(--accent-warm)/0.88)] text-[hsl(var(--accent-warm-foreground))]" data-testid="save-list-btn">
                    Create List
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
      {/* Segmentation Panel */}
      {showSegment && (
        <Card className="bg-[hsl(var(--pane))] border border-white/10 mb-6" style={{boxShadow: 'var(--shadow-elev-1)'}}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Segment by Tags</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowSegment(false); setSegmentTags([]); setSegmentResult(null); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quick Create & Bulk Tag */}
            <div className="flex items-center gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Type a tag name (e.g., CISO, enterprise, partner)"
                className="flex-1 bg-secondary/40 text-sm h-8"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newTag.trim() && selectedList) {
                    handleBulkTag(newTag.trim());
                  }
                }}
                data-testid="segment-tag-input"
              />
              <Button
                size="sm"
                className="h-8 text-xs bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90"
                disabled={!newTag.trim() || !selectedList}
                onClick={() => newTag.trim() && selectedList && handleBulkTag(newTag.trim())}
                data-testid="bulk-tag-btn"
              >
                <Tag className="w-3 h-3 mr-1" />
                Tag all in list
              </Button>
            </div>
            {!selectedList && (
              <p className="text-[10px] text-amber-400">Select a list first to bulk-tag contacts</p>
            )}

            {/* Existing Tags */}
            <div>
              <p className="text-[10px] text-muted-foreground mb-2 uppercase tracking-wider font-medium">
                {allTags.length > 0 ? `Available Tags (${allTags.length})` : 'No tags yet — create one above'}
              </p>
              <div className="flex flex-wrap gap-2">
                {allTags.map(t => (
                  <Badge key={t.tag}
                    className={`cursor-pointer transition-colors duration-150 ${
                      segmentTags.includes(t.tag)
                        ? 'bg-[hsl(var(--accent))] text-white'
                        : 'bg-secondary text-foreground hover:bg-secondary/80'
                    }`}
                    onClick={() => toggleSegmentTag(t.tag)}
                    data-testid={`segment-tag-${t.tag}`}
                  >
                    {t.tag} ({t.count})
                  </Badge>
                ))}
              </div>
            </div>

            {/* Segment Preview */}
            {segmentTags.length > 0 && (
              <div className="flex items-center gap-3 pt-2 border-t border-border/50">
                <Button size="sm" onClick={handleSegmentPreview} data-testid="preview-segment-btn">
                  Preview Segment
                </Button>
                {segmentResult && (
                  <span className="text-sm text-muted-foreground">
                    {segmentResult.count} contact{segmentResult.count !== 1 ? 's' : ''} match
                  </span>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Lists sidebar */}
        <div className="space-y-2">
          {lists.map(l => (
            <div key={l.id}
              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors duration-150 ${
                selectedList === l.id ? 'bg-secondary/60 border border-border' : 'hover:bg-secondary/30'
              }`}
              onClick={() => setSelectedList(l.id)}
              data-testid={`list-item-${l.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{l.name}</p>
                  <p className="text-xs text-muted-foreground">{l.contact_count} contacts</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteList(l.id); }}>
                <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
              </Button>
            </div>
          ))}
          {lists.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No lists yet</p>}
        </div>

        {/* Contacts table */}
        <div className="lg:col-span-3">
          <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {selectedList ? `Contacts (${contacts.length})` : 'Select a list'}
                </CardTitle>
                {selectedList && (
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search contacts or tags..." className="pl-9 bg-secondary/40 w-56" data-testid="contacts-search-input" />
                    </div>
                    <Button variant="outline" size="sm" onClick={handleExportContacts} data-testid="export-contacts-btn">
                      <Download className="w-4 h-4 mr-1" /> Export
                    </Button>
                    <label className="cursor-pointer">
                      <input type="file" accept=".csv" onChange={handleImportCSV} className="hidden" data-testid="csv-import-input" />
                      <Button variant="outline" size="sm" asChild data-testid="contacts-import-csv-button">
                        <span><Upload className="w-4 h-4 mr-1" /> Import CSV</span>
                      </Button>
                    </label>
                    <Dialog open={showNewContact} onOpenChange={setShowNewContact}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-[hsl(var(--accent))] text-white" data-testid="add-contact-btn">
                          <Plus className="w-4 h-4 mr-1" /> Add
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Add Contact</DialogTitle><DialogDescription>Add a new contact to this list</DialogDescription></DialogHeader>
                        <div className="space-y-3">
                          <div><Label>Email *</Label><Input value={newContact.email} onChange={(e) => setNewContact({...newContact, email: e.target.value})} placeholder="email@example.com" data-testid="contact-email-input" /></div>
                          <div><Label>First Name</Label><Input value={newContact.first_name} onChange={(e) => setNewContact({...newContact, first_name: e.target.value})} placeholder="John" /></div>
                          <div><Label>Last Name</Label><Input value={newContact.last_name} onChange={(e) => setNewContact({...newContact, last_name: e.target.value})} placeholder="Doe" /></div>
                          <div><Label>Company</Label><Input value={newContact.company} onChange={(e) => setNewContact({...newContact, company: e.target.value})} placeholder="Acme Inc." /></div>
                          <Button onClick={handleAddContact} className="w-full bg-[hsl(var(--accent))] text-white" data-testid="save-contact-btn">Add Contact</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {selectedList ? (
                filteredContacts.length > 0 ? (
                  <Table data-testid="contacts-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Tags</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredContacts.map(c => (
                        <TableRow key={c.id} className="table-row-hover">
                          <TableCell className="font-mono text-sm">{c.email}</TableCell>
                          <TableCell className="text-sm">{c.first_name} {c.last_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{c.company}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1 items-center">
                              {(c.tags || []).map(tag => (
                                <Badge key={tag} className="bg-secondary text-foreground text-[10px] px-1.5 py-0 group">
                                  {tag}
                                  <X className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity duration-150"
                                    onClick={(e) => { e.stopPropagation(); handleRemoveTag(c.id, tag); }} />
                                </Badge>
                              ))}
                              <Button variant="ghost" size="sm" className="h-5 w-5 p-0"
                                onClick={() => { setTagContactId(c.id); setShowTagDialog(true); }}>
                                <Tag className="w-3 h-3 text-muted-foreground" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={c.subscribed !== false ? 'bg-[hsl(var(--status-success)/0.15)] text-[hsl(var(--status-success))]' : 'bg-secondary text-muted-foreground'}>
                              {c.subscribed !== false ? 'Active' : 'Unsubscribed'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(c.id)} data-testid={`delete-contact-${c.id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12">
                    <Users className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground">No contacts in this list</p>
                  </div>
                )
              ) : (
                <div className="text-center py-12">
                  <p className="text-sm text-muted-foreground">Select or create a list to view contacts</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Tag Dialog */}
      <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tag</DialogTitle>
            <DialogDescription>Add a tag to categorize this contact</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tag Name</Label>
              <Input value={newTag} onChange={(e) => setNewTag(e.target.value)}
                placeholder="e.g., enterprise, decision-maker, CISO"
                onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                data-testid="tag-name-input" />
            </div>
            {allTags.length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground">Quick add existing tag:</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {allTags.slice(0, 10).map(t => (
                    <Badge key={t.tag} className="bg-secondary text-foreground cursor-pointer hover:bg-secondary/80 transition-colors duration-150"
                      onClick={() => { setNewTag(t.tag); }}>
                      {t.tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={handleAddTag} className="w-full bg-[hsl(var(--accent))] text-white" data-testid="save-tag-btn">
              <Tag className="w-4 h-4 mr-2" /> Add Tag
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
